import { useState, useEffect } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
// ProceduresService no longer exists - using stub implementation
import type { ProcedureDetailsResponse } from './ProcedureDetailsView'
import { useToast } from '@/hooks/useToast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AgentProxyService } from '@/services/agentProxyService'
import { databaseKeys } from '@/hooks/useDatabase'

interface EditProcedurePanelProps {
  connection: DatabaseConnection
  database: string
  procedureName: string
  schema?: string
  initialProcedure?: ProcedureDetailsResponse
  onClose: () => void
  onSuccess: () => void
}

export function EditProcedurePanel({ 
  connection, 
  database, 
  procedureName, 
  schema, 
  initialProcedure,
  onClose,
  onSuccess 
}: EditProcedurePanelProps) {
  const [formData, setFormData] = useState({
    name: initialProcedure?.metadata?.procedure_name || procedureName,
    definition: initialProcedure?.definition || '',
    schema: initialProcedure?.metadata?.schema_name || schema || (connection.type === 'mysql' ? database : 'public')
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (initialProcedure) {
      setFormData({
        name: initialProcedure.metadata?.procedure_name || procedureName,
        definition: initialProcedure.definition || '',
        schema: initialProcedure.metadata?.schema_name || schema || (connection.type === 'mysql' ? database : 'public')
      })
    }
  }, [initialProcedure, procedureName, schema, connection.type, database])

  const alterProcedureMutation = useMutation({
    mutationFn: async () => {
      const language = connection.type === 'postgresql' ? 'plpgsql' : 'SQL'
      return AgentProxyService.alterProcedure(
        connection.id,
        database,
        procedureName,
        {
          body: formData.definition,
          language
        },
        formData.schema
      )
    },
    onSuccess: () => {
      showToast('success', 'Procedure updated successfully')
      // Invalidate queries to refresh the procedure details
      void queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'procedure-details', connection.id, database, procedureName, schema] })
      // Also invalidate the procedures list to update the tree view
      void queryClient.invalidateQueries({ queryKey: [...databaseKeys.all, 'procedures', connection.id, database, schema] })
      onSuccess()
      onClose()
    },
    onError: (error: unknown) => {
      console.error('Failed to update procedure:', error)
      const message = error instanceof Error ? error.message : 'Failed to update procedure'
      showToast('error', message)
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.definition.trim()) {
      showToast('error', 'Procedure definition cannot be empty')
      return
    }

    setIsSubmitting(true)
    try {
      await alterProcedureMutation.mutateAsync()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 w-1/2 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <div className="text-2xl mr-3">🔧</div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit Procedure
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Modify the stored procedure definition
            </p>
          </div>
        </div>
        <button type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Edit Procedure Definition
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Modify the procedure body. The procedure name and parameters cannot be changed.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={(e) => void handleSubmit(e)} className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* Procedure Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Procedure Name
            </label>
            <input
              type="text"
              value={formData.name}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
            />
          </div>

          {/* Schema */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Schema
            </label>
            <input
              type="text"
              value={formData.schema}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
            />
          </div>

          {/* Procedure Definition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Procedure Definition
            </label>
            <textarea
              value={formData.definition}
              onChange={(e) => { setFormData({ ...formData, definition: e.target.value }) }}
              rows={15}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter procedure definition..."
              required
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Enter the procedure body (BEGIN...END block). Do not include the CREATE PROCEDURE statement.
            </p>
          </div>
        </div>
      </form>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={(e) => void handleSubmit(e)}
          disabled={isSubmitting || !formData.definition.trim()}
          className={`flex items-center px-4 py-2 text-white rounded-lg transition-colors ${
            isSubmitting || !formData.definition.trim()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700'
          }`}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Update Procedure
            </>
          )}
        </button>
      </div>
    </div>
  )
}